import i18n from "i18next";
import { usePublisherProfiles } from "@/hooks/usePublisherProfiles";
import { testIds } from "@/testIds";
import styles from "../DocumentStamp.module.scss";

export interface PublishWithProps {
  /** The document's profile id, or null/undefined for none. */
  value: string | null | undefined;
  /** Picks a profile; "" goes back to Lilia's own layout. */
  onChange: (profileId: string) => void;
  disabled?: boolean;
  /** A co-author: the venue is the owner's to choose (the API refuses it too), said under the choice. */
  ownerOnly?: boolean;
}

const T = testIds.publishWith;

/**
 * "Publish with…" (publisher templates phase 1, 25 Oct 2026): the venue and stage a document is written
 * for. The document then prints in the publisher's own class, with its front matter and bibliography
 * style, and none of Lilia's layout. Under the choice: what it sets and what the publisher requires.
 */
export function PublishWith({ value, onChange, disabled, ownerOnly }: PublishWithProps) {
  const { profiles, installed = {} } = usePublisherProfiles();
  const current = profiles.find((p) => p.id === value) ?? null;
  const publishers = [...new Set(profiles.map((p) => p.publisher))];
  const stage = (s: string) => i18n.t(s === "review" ? "editor:stamp.publishStageReview" : "editor:stamp.publishStageFinal");
  // Review | Camera-ready (phase 2, 25 Oct 2026): the venue's first profile at each stage, when it has
  // both. Moving keeps the venue; the class stays, its options change.
  const stages = current
    ? (["review", "final"] as const)
        .map((st) => profiles.find((p) => p.family === current.family && p.stage === st))
        .filter((p): p is NonNullable<typeof p> => !!p)
    : [];
  const field = (f: string) => i18n.t(`editor:stamp.publishNeeds_${f}`, { defaultValue: f });

  return (
    <div className={styles.section} data-testid={T.section}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>{i18n.t("editor:stamp.publishWith")}</span>
        {disabled && <span className={styles.readOnlyPip}>{i18n.t("editor:stamp.readOnly")}</span>}
      </div>
      <select
        data-testid={T.select}
        aria-label={i18n.t("editor:stamp.publishWith")}
        value={current?.id ?? ""}
        disabled={disabled || ownerOnly || profiles.length === 0}
        onChange={(e) => onChange(e.target.value)}
        className={styles.languageSelect}
      >
        <option value="">{i18n.t("editor:stamp.publishNone")}</option>
        {publishers.map((pub) => (
          <optgroup key={pub} label={pub}>
            {profiles.filter((p) => p.publisher === pub).map((p) => (
              <option key={p.id} value={p.id}>{`${p.venue} · ${stage(p.stage)}`}</option>
            ))}
          </optgroup>
        ))}
      </select>
      {ownerOnly && <p className={styles.publishHint} data-testid={T.ownerOnly}>{i18n.t("editor:stamp.publishOwnerOnly")}</p>}
      {current && stages.length > 1 && (
        <div className={styles.stageSwitch} role="group" aria-label={i18n.t("editor:stamp.publishStage")}>
          {stages.map((p) => (
            <button
              key={p.stage}
              type="button"
              data-testid={T.stage}
              data-stage={p.stage}
              aria-pressed={p.stage === current.stage}
              disabled={disabled || ownerOnly || p.stage === current.stage}
              onClick={() => onChange(p.id)}
            >
              {i18n.t(p.stage === "review" ? "editor:stamp.publishStageReviewButton" : "editor:stamp.publishStageFinalButton")}
            </button>
          ))}
        </div>
      )}
      {current ? (
        <dl className={styles.publishFacts} data-testid={T.facts} data-profile={current.id}>
          <dt>{i18n.t("editor:stamp.publishClass")}</dt>
          <dd>
            <code>{current.documentClass}{current.classOptions ? ` [${current.classOptions}]` : ""}</code> · {current.templateVersion}
            {/* The class on the server, when Lilia knows it (the template update service). */}
            {installed[current.documentClass] && (
              <span data-testid={T.installed}> · {i18n.t("editor:stamp.publishInstalled", { version: installed[current.documentClass] })}</span>
            )}
          </dd>
          <dt>{i18n.t("editor:stamp.publishReferences")}</dt>
          <dd><code>{current.bibliographyStyle}</code></dd>
          <dt>{i18n.t("editor:stamp.publishNeeds")}</dt>
          <dd>{current.requiredMetadata.map(field).join(", ")}</dd>
          <dd className={styles.publishLink}>
            <a href={current.guidelinesUrl} target="_blank" rel="noreferrer" data-testid={T.guidelines}>
              {i18n.t("editor:stamp.publishGuidelines", { publisher: current.publisher })} ↗
            </a>
          </dd>
        </dl>
      ) : (
        <p className={styles.publishHint}>{i18n.t("editor:stamp.publishNoneHint")}</p>
      )}
    </div>
  );
}
